import api from "../api.service"
import authService from "../auth/auth.service"
import omit from "lodash/omit"
import { getCache, setCache } from "../../utils/cache"

import {} from "../../services/api/utils"

import queryString from "query-string"

const cleanOrgId = (id) => {
    if (typeof id === "object" && id !== null && "_id" in id) {
        return id._id // Return _id if it's a property in the object
    }
    return typeof id === "string" ? id : null // Ensure it's a string, otherwise return null
}

class OrgApi {
    /**
     * POST a new org
     *
     * @param payload {Object}
     * @param payload.title {String} Org title
     * @param [payload.tagline] {String} Brand tagline
     * @param [payload.about] {String} About the organization
     * @param [payload.logo] {*} A logo. NOT SUPPORTED YET
     * @param [payload.domains] {Array<String>} Array of restricted domains, of the format "domain.com" (INVALID: www.domain.com, http://domain.com)
     * @param [payload.inviteDomains] {Array<String>} Array of restricted email domains that can be invited to this organization
     * @param [payload.whoCanInvite] {Array<Number>} Array of permissions that can invite to this organization
     *
     * @param [payload.people] {Object} Options for the person creating the org
     * @param [payload.people.role] {String} Role of creator
     * @param [payload.people.email] {String} Email of creator
     * @return {Promise<Object>}
     */
    createOrg(payload) {
        return api
            .post("/org", payload)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    uploadLogo(orgId, file) {
        return api
            .uploadFileSingle(`/org/${cleanOrgId(orgId)}/logo`, file, {
                name: "orgLogo",
            })
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    uploadSecondaryLogo(orgId, file) {
        return api
            .uploadFileSingle(`/org/${cleanOrgId(orgId)}/secondary-logo`, file, {
                name: "secondaryOrgLogo",
            })
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    buildOrgQuery(options = {}) {
        const baseFlags = {
            withArchivedPeople: options.withArchivedPeople === true ? "1" : "0",
            withArchivedDepartments: "1",
            withArchivedOffices: "1",
            withArchivedPrograms: "1",
            withArchivedRoles: "1",
        }
        if (options.withArchivedClients === true) {
            baseFlags.withArchivedClients = "1"
        }
        return queryString.stringify({
            ...omit(options, ["withArchivedClients", "withArchivedPeople"]),
            ...baseFlags,
        })
    }

    gtSyncPerson(orgId, personId) {
        return api
            .post(`/org/${cleanOrgId(orgId)}/gt/sync/person/${personId}`)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    gtResetLoginProvider(orgId, email) {
        return api
            .post(`/org/${cleanOrgId(orgId)}/gt/auth/reset-login-provider`, { email })
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    async getOrg(orgId, myOrgUserId, options = {}) {
        try {
            const orgQs = this.buildOrgQuery(options)
            let data = []

            try {
                data = await api.get(`/org/${cleanOrgId(orgId)}?${orgQs}`)
            } catch (e) {
                throw api.getResponseError(e)
            }

            return data.data
        } catch (err) {
            throw api.getResponseError(err)
        }
    }

    async getOrgPublic(orgId) {
        return api
            .get(`/org/${cleanOrgId(orgId)}/p`)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    async getOrgList(app) {
        try {
            const orgQs = this.buildOrgQuery({})
            const { data } = await api.get(`/org?${orgQs}`)

            return data
        } catch (err) {
            throw api.getResponseError(err)
        }
    }

    /**
     * PUT an org
     * @param orgId {String}
     * @param data {Object}
     * @return {Promise<Object>}
     */
    updateOrg(orgId, data) {
        const qs = queryString.stringify({
            withArchivedDepartments: "1",
            withArchivedOffices: "1",
            withArchivedPrograms: "1",
            withArchivedRoles: "1",
        })
        return api
            .put(`/org/${cleanOrgId(orgId)}?${qs}`, data)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    /**
     * DELETE an org
     * @param orgId {String}
     * @param [options] {Object}
     * @return {Promise<void>}
     */
    deleteOrg(orgId, options = {}) {
        const qs = queryString.stringify(options)
        return api
            .delete(`/org/${cleanOrgId(orgId)}?${qs}`)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    getOrgPerson(orgId, personId) {
        return api
            .get(`/org/${cleanOrgId(orgId)}/people/${personId}`)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    updateOrgPeople(orgId, personId, data) {
        return api
            .put(`/org/${cleanOrgId(orgId)}/people/${personId}`, data)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    /**
     * Many people's HR details in one request (mAX's update_people).
     *
     * @param orgId {String}
     * @param people {Array<{personId: String, change: Object}>} Up to 200
     * @return {Promise<{people: Object[], failed: Array<{personId: String, code: String, message: String}>}>}
     */
    updateOrgPeopleBatch(orgId, people) {
        return api
            .put(`/org/${cleanOrgId(orgId)}/people`, { people })
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    /**
     * A person's role changed to one with a different cost: preview or apply it on
     * their missions from an effective Sunday (api/orgPerson/orgPerson.roleCostChange).
     *
     * @param data {{fromRole: String, toRole: String, effectiveDate: Number, mode: "preview"|"apply"}}
     */
    roleCostChange(orgId, personId, data) {
        return api
            .post(`/org/${cleanOrgId(orgId)}/people/${personId}/role-cost-change`, data)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    updateOrgPeopleEmail(orgId, personId, newEmail) {
        return api
            .put(`/org/${cleanOrgId(orgId)}/people/${personId}/email`, { email: newEmail })
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    /**
     *
     * @param orgId
     * @param orgPeopleId
     * @param [options] {Object}
     * @param [options.removeFromMissions] {Boolean} If true, will remove the person from all org missions as well
     * @return {Promise<*>}
     */
    deleteOrgPeople(orgId, orgPeopleId, options = {}) {
        const qs = queryString.stringify(options)
        return api
            .delete(`/org/${cleanOrgId(orgId)}/people/${orgPeopleId}?${qs}`)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    addGoal(orgId, data) {
        return api
            .post(`/org/${cleanOrgId(orgId)}/goal`, data)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    updateGoal(orgId, goalId, data) {
        return api
            .put(`/org/${cleanOrgId(orgId)}/goal/${goalId}`, data)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    deleteGoal(orgId, goalId) {
        return api
            .delete(`/org/${cleanOrgId(orgId)}/goal/${goalId}`)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    /**
     * @param orgId
     * @param inviteeEmail
     * @param data Invitation data
     */
    createInvite(orgId, inviteeEmail, data) {
        return api
            .post(`/org/${cleanOrgId(orgId)}/invite`, Object.assign({}, data, { inviteeEmail }))
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    async handleInvite(token, payload) {
        const user = await authService.getCurrentUser()

        return (
            api
                .post(`/org-invite/handle/${token}`, payload)
                // Refresh token in case email was verified during invite handle
                .then(() => user.getIdToken(true))
                .catch((err) => {
                    throw api.getResponseError(err)
                })
        )
    }

    getInviteByToken(token) {
        return api
            .get(`/org-invite/token/${token}`)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    async acceptInvite(inviteId) {
        try {
            const orgX = await api.put(`/org-invite/${inviteId}/accept`)

            //const clientList = (await api.get(`/org/client/list?org=${org._id}`))?.data
            return orgX.data
        } catch (err) {
            throw api.getResponseError(err)
        }
    }

    rejectInvite(inviteId) {
        return api
            .put(`/org-invite/${inviteId}/reject`)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    leaveRequestNotify(orgId) {
        return api
            .post(`/org/${cleanOrgId(orgId)}/leave/notify`)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    /**
     * ============================================================
     * Org Workspace
     * ============================================================
     */

    /**
     * @param orgId {string}
     * @param [options] {object}
     * @param [options.selectFields] {string[]} A list of properties to return for each mission
     * @param [options.isActive] {boolean} If true returns only active missions. If false, returns only completed missions. If not specified, returns both active and completed.
     * @param [options.completedBufferDate] {number} Buffer date to return missions as active, even if mission stop is in the past
     * @param [options.staleDate] {number} Buffer date to return missions as active, even if mission plan end date is in the past
     * @param [options.startDate] {number} If specified, will return all missions after this date. If "endDate" is specified as well, will return missions within the range.
     * @param [options.endDate] {number} If specified, will return all missions before this date. If "startDate" is specified as well, will return missions within the range.
     * @param [options.cached] {boolean} If true, it will try to return a cached version
     * @param [options.cacheEnabled] {boolean} Enable caching. Defaults to true
     * @return {Promise<string>}
     */
    async getOrgWorkspaceMissions(orgId, options = {}) {
        const { cached = false, cacheEnabled = true } = options
        const qs = queryString.stringify(omit(options, ["cached", "cacheEnabled"]))
        const url = `/org/${cleanOrgId(orgId)}/workspace/mission?${qs}`

        if (cacheEnabled === true && cached === true) {
            try {
                const { data } = await getCache(url)

                if (data) {
                    return data
                }
            } catch (err) {
                // Nothing
                return
            }
        }

        return api
            .get(url)
            .then(async (res) => {
                if (cacheEnabled === true) {
                    setCache(res.data, url).catch((err) => {
                        // Nothing...
                    })
                }
                return res.data
            })
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    /**
     * @param orgId {string}
     * @param [options] {object}
     * @param [options.selectFields] {string[]} A list of properties to return for each mission
     * @param [options.selectTypes] {string[]} E.g. ["person", "role"]
     * @param [options.isActive] {boolean} If true returns only active missions. If false, returns only completed missions. If not specified, returns both active and completed.
     * @param [options.completedBufferDate] {number} Buffer date to return missions as active, even if mission stop is in the past
     * @param [options.staleDate] {number} Buffer date to return missions as active, even if mission plan end date is in the past
     * @param [options.startDate] {number} If specified, will return all missions after this date. If "endDate" is specified as well, will return missions within the range.
     * @param [options.endDate] {number} If specified, will return all missions before this date. If "startDate" is specified as well, will return missions within the range.
     * @return {Promise<string>}
     */
    getOrgWorkspaceMissionPlanItems(orgId, options = {}) {
        const qs = queryString.stringify(options)
        return api
            .get(`/org/${cleanOrgId(orgId)}/workspace/missionPlan?${qs}`)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    /**
     * ============================================================
     * Org Vendor
     * ============================================================
     */

    /**
     * POST a new org vendor
     *
     * @param orgId {string} Org _id
     * @param payload {Object}
     * @param payload.title {String} Vendor title
     * @param payload.vendorId {String} A unique identifying code for the vendor (e.g. a tax id or generated code)
     * @return {Promise<*>}
     */
    createOrgVendor(orgId, payload) {
        return api
            .post(`/org/${cleanOrgId(orgId)}/vendor`, payload)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    /**
     * GET a fresh, currently-unused vendorId candidate (e.g. "VEN-12345") to prefill the
     * add-vendor form with. Still re-checked for uniqueness server-side on actual create.
     *
     * @param orgId {string} Org _id
     * @return {Promise<{vendorId: string}>}
     */
    generateOrgVendorId(orgId) {
        return api
            .get(`/org/${cleanOrgId(orgId)}/vendor/generateId`)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    /**
     * POST a batch of pre-mapped CSV rows to bulk-create org vendors.
     *
     * @param orgId {string} Org _id
     * @param rows {Array<Object>} Vendor field objects, one per CSV row
     * @return {Promise<{imported: number, errorCount: number, errors: Array, value: Array}>}
     */
    bulkImportOrgVendors(orgId, rows) {
        return api
            .post(`/org/${cleanOrgId(orgId)}/vendor/bulkImport`, { rows })
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    /**
     * PUT an org vendor
     *
     * @param orgId {string} Org _id
     * @param vendorId {string} Vendor _id
     * @param payload {Object}
     * @param [payload.title] {String} Vendor title
     * @param [payload.vendorId] {String} A unique identifying code for the vendor (e.g. a tax id or generated code)
     * @param [payload.status] {String} "active" or "disabled"
     * @return {Promise<Object>}
     */
    updateOrgVendor(orgId, vendorId, payload) {
        return api
            .put(`/org/${cleanOrgId(orgId)}/vendor/${vendorId}`, payload)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    /**
     * POST - manually (re)sends the vendor magic-link onboarding email.
     * 429 if one was already sent to this vendor in the last 24h, 400 if the
     * vendor has no valid contact email or already has everything on file.
     *
     * @param orgId {string} Org _id
     * @param vendorId {string} Vendor _id
     * @return {Promise<{success: boolean}>}
     */
    sendOrgVendorOnboardingEmail(orgId, vendorId) {
        return api
            .post(`/org/${cleanOrgId(orgId)}/vendor/${vendorId}/onboard`)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    /**
     * GET a short-lived signed URL to view/download a tax document a vendor
     * submitted through the onboarding flow.
     *
     * @param orgId {string} Org _id
     * @param vendorId {string} Vendor _id
     * @param docId {string} taxDocuments[].id
     * @return {Promise<{url: string}>}
     */
    getOrgVendorTaxDocumentSignedUrl(orgId, vendorId, docId) {
        return api
            .get(`/org/${cleanOrgId(orgId)}/vendor/${vendorId}/taxDocument/${docId}/signedUrl`)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    /**
     * POST - approve or reject a vendor awaiting approval.
     *
     * @param orgId {string} Org _id
     * @param vendorId {string} Vendor _id
     * @param decision {"approved"|"rejected"}
     * @return {Promise<Object>} The updated vendor
     */
    decideOrgVendorApproval(orgId, vendorId, decision) {
        return api
            .post(`/org/${cleanOrgId(orgId)}/vendor/${vendorId}/approval`, { decision })
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    updateOrgVendorApprovalComments(orgId, vendorId, comments) {
        return api
            .put(`/org/${cleanOrgId(orgId)}/vendor/${vendorId}/approval/comments`, { comments })
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    /**
     * @param orgId {string} Org _id
     * @param vendorId {string} Vendor _id
     * @param files {File|Array<File>}
     * @param [options] {Object}
     * @param [options.onUploadProgress] {Function}
     * @return {Promise<Object>} The updated vendor
     */
    addOrgVendorApprovalDocuments(orgId, vendorId, files, options = {}) {
        return api
            .uploadFileMulti(`/org/${cleanOrgId(orgId)}/vendor/${vendorId}/approval/document`, files, options)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    removeOrgVendorApprovalDocument(orgId, vendorId, docId) {
        return api
            .delete(`/org/${cleanOrgId(orgId)}/vendor/${vendorId}/approval/document/${docId}`)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    getOrgVendorApprovalDocumentSignedUrl(orgId, vendorId, docId) {
        return api
            .get(`/org/${cleanOrgId(orgId)}/vendor/${vendorId}/approval/document/${docId}/signedUrl`)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    /**
     * ============================================================
     * Org PO Number
     * ============================================================
     */

    /**
     * POST a new org PO number
     *
     * @param orgId {string} Org _id
     * @param payload {Object}
     * @param payload.poNumber {String} PO number
     * @param payload.vendor {String} Vendor _id
     * @param payload.client {String} Client _id
     * @param payload.type {String} "payable" or "receivable"
     * @param [payload.status] {String} "approved" | "rejected" | "paid" | "pending" | "draft"
     * @return {Promise<*>}
     */
    createOrgPoNumber(orgId, payload) {
        return api
            .post(`/org/${cleanOrgId(orgId)}/poNumber`, payload)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    /**
     * PUT an org PO number
     *
     * @param orgId {string} Org _id
     * @param poNumberId {string} PO Number _id
     * @param payload {Object}
     * @return {Promise<Object>}
     */
    updateOrgPoNumber(orgId, poNumberId, payload) {
        return api
            .put(`/org/${cleanOrgId(orgId)}/poNumber/${poNumberId}`, payload)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    /**
     * POST a batch of pre-mapped CSV rows to bulk-create org PO numbers. Each row
     * references its vendor/project/GL code by name or code (resolved server-side).
     *
     * @param orgId {string} Org _id
     * @param rows {Array<Object>} PO number field objects, one per CSV row
     * @return {Promise<{imported: number, errorCount: number, errors: Array, value: Array}>}
     */
    bulkImportOrgPoNumbers(orgId, rows) {
        return api
            .post(`/org/${cleanOrgId(orgId)}/poNumber/bulkImport`, { rows })
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    /**
     * ============================================================
     * Org GL Code
     * ============================================================
     */

    /**
     * POST a new org GL code
     *
     * @param orgId {string} Org _id
     * @param payload {Object}
     * @param payload.code {String} GL code, e.g. "6100"
     * @param payload.name {String} Account name, e.g. "Software & Subscriptions"
     * @param [payload.category] {String} "operating_expense" | "asset" | "liability" | "direct_cost" | "revenue" | "cost_of_sales" | "equity"
     * @param [payload.department] {String}
     * @param [payload.status] {String} "active" | "inactive" - defaults to "active"
     * @return {Promise<*>}
     */
    createOrgGlCode(orgId, payload) {
        return api
            .post(`/org/${cleanOrgId(orgId)}/glCode`, payload)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    /**
     * PUT an org GL code
     *
     * @param orgId {string} Org _id
     * @param glCodeId {string} GL Code _id
     * @param payload {Object}
     * @return {Promise<Object>}
     */
    updateOrgGlCode(orgId, glCodeId, payload) {
        return api
            .put(`/org/${cleanOrgId(orgId)}/glCode/${glCodeId}`, payload)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    /**
     * POST a CSV import batch of org GL codes - see glCode.controller.js#bulkImportGlCodes.
     * Validation/duplicate rows are reported back rather than thrown, since a partial
     * import is a normal, expected outcome here.
     *
     * @param orgId {string} Org _id
     * @param rows {Object[]} Parsed { code, name, category, department, status } rows
     * @return {Promise<{imported: Number, errorCount: Number, errors: Object[], value: Object[]}>}
     */
    bulkImportOrgGlCodes(orgId, rows) {
        return api
            .post(`/org/${cleanOrgId(orgId)}/glCode/bulkImport`, { rows })
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    /**
     * GET the org's equipment - see equipment.controller.js. Archived units only
     * come back with `archived`.
     *
     * @param orgId {string} Org _id
     * @param [options.archived] {Boolean}
     * @return {Promise<Object[]>}
     */
    getOrgEquipment(orgId, { archived } = {}) {
        return api
            .get(`/org/${cleanOrgId(orgId)}/equipment/items${archived ? "?archived=1" : ""}`)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    /**
     * POST a piece of equipment
     *
     * @param orgId {string} Org _id
     * @param payload {Object} { title, sku, type, description, rate, rateTime, billRate, billUnit }
     * @return {Promise<Object>}
     */
    createOrgEquipment(orgId, payload) {
        return api
            .post(`/org/${cleanOrgId(orgId)}/equipment/items`, payload)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    /**
     * PUT a piece of equipment - null rates fall back to the type's.
     *
     * @param orgId {string} Org _id
     * @param equipmentId {string} Equipment _id
     * @param payload {Object}
     * @return {Promise<Object>}
     */
    updateOrgEquipment(orgId, equipmentId, payload) {
        return api
            .put(`/org/${cleanOrgId(orgId)}/equipment/items/${equipmentId}`, payload)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    /**
     * The equipment endpoints below all answer the same way: the data, or the API's
     * error ({ code, message, data }) thrown. `_equipment` builds their path.
     */
    _equipment(orgId, path = "") {
        return `/org/${cleanOrgId(orgId)}/equipment${path}`
    }

    _equipmentCall(promise) {
        return promise
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    /**
     * GET how asset ids are generated: { prefix, padLength, nextNumber, nextAssetId }.
     */
    getOrgEquipmentSettings(orgId) {
        return this._equipmentCall(api.get(this._equipment(orgId, "/settings")))
    }

    /**
     * PUT any of { prefix, padLength, nextNumber } - answers with the settings.
     */
    updateOrgEquipmentSettings(orgId, payload) {
        return this._equipmentCall(api.put(this._equipment(orgId, "/settings"), payload))
    }

    /**
     * GET one unit with its maintenance/checkout logs and files - the profile card.
     */
    getOrgEquipmentItem(orgId, equipmentId) {
        return this._equipmentCall(api.get(this._equipment(orgId, `/items/${equipmentId}`)))
    }

    /**
     * GET a unit's change history, newest first: [{ action, changes: { field: { from, to } }, by, at }]
     */
    getOrgEquipmentHistory(orgId, equipmentId) {
        return this._equipmentCall(api.get(this._equipment(orgId, `/items/${equipmentId}/history`)))
    }

    /**
     * GET every booking of a unit (mission populated), newest first.
     */
    getOrgEquipmentBookingHistory(orgId, equipmentId) {
        return this._equipmentCall(api.get(this._equipment(orgId, `/items/${equipmentId}/bookings`)))
    }

    /**
     * GET per-unit list figures from the stored monthly data: { [equipmentId]: {
     * bookedDays, utilization (this month, 0..1), bookedNow, upcoming: [{ startDate,
     * endDate }], revenue, internalCost, costs, net } }. Units with no data are absent.
     */
    getOrgEquipmentStats(orgId) {
        return this._equipmentCall(api.get(this._equipment(orgId, "/items/stats")))
    }

    /**
     * POST one Equipment Map view load - before Google Maps is loaded. Throws
     * equipment/map_limit_reached (402) when a Free/trial allowance is used up.
     */
    recordEquipmentMapLoad(orgId) {
        return this._equipmentCall(api.post(this._equipment(orgId, "/map/loads")))
    }

    /**
     * GET this month's map loads: { loads, included, hardCap, overageUnitPrice, overLoads, overageCost }.
     */
    getEquipmentMapUsage(orgId) {
        return this._equipmentCall(api.get(this._equipment(orgId, "/map/usage")))
    }

    /**
     * PUT a unit's map pin { lat, lng } - { lat: null } removes it. Answers with the unit.
     */
    setOrgEquipmentLocation(orgId, equipmentId, { lat, lng }) {
        return this._equipmentCall(api.put(this._equipment(orgId, `/items/${equipmentId}/location`), { lat, lng }))
    }

    /**
     * GET the CSV export: { columns: String[], rows: Object[] }.
     */
    exportOrgEquipment(orgId, { archived } = {}) {
        return this._equipmentCall(api.get(this._equipment(orgId, `/items/export${archived ? "?archived=1" : ""}`)))
    }

    /**
     * POST parsed CSV rows: { created, updated, errors: [{ row, message }] }.
     */
    importOrgEquipment(orgId, rows) {
        return this._equipmentCall(api.post(this._equipment(orgId, "/items/import"), { rows }))
    }

    /**
     * Maintenance log / checkout log entries. `log` is "maintenance" or "checkouts".
     * Each answers with the whole unit.
     */
    addOrgEquipmentLogEntry(orgId, equipmentId, log, payload) {
        return this._equipmentCall(api.post(this._equipment(orgId, `/items/${equipmentId}/${log}`), payload))
    }

    updateOrgEquipmentLogEntry(orgId, equipmentId, log, entryId, payload) {
        return this._equipmentCall(
            api.put(this._equipment(orgId, `/items/${equipmentId}/${log}/${entryId}`), payload),
        )
    }

    removeOrgEquipmentLogEntry(orgId, equipmentId, log, entryId) {
        return this._equipmentCall(api.delete(this._equipment(orgId, `/items/${equipmentId}/${log}/${entryId}`)))
    }

    /**
     * Upload files to a unit. kind: photos | attachments | calibrationCertificate |
     * sdsDocument | maintenance (with entryId). Answers with the whole unit.
     */
    uploadOrgEquipmentFiles(orgId, equipmentId, kind, files, { entryId, onUploadProgress } = {}) {
        const qs = new URLSearchParams({ kind, ...(entryId ? { entryId } : {}) }).toString()

        return this._equipmentCall(
            api.uploadFileMulti(this._equipment(orgId, `/items/${equipmentId}/files?${qs}`), files, {
                onUploadProgress,
            }),
        )
    }

    removeOrgEquipmentFile(orgId, equipmentId, kind, fileId, { entryId } = {}) {
        const qs = new URLSearchParams({ kind, ...(entryId ? { entryId } : {}) }).toString()

        return this._equipmentCall(api.delete(this._equipment(orgId, `/items/${equipmentId}/files/${fileId}?${qs}`)))
    }

    /**
     * GET the equipment workflows: { tier, channels, workflows: { [workflow]: { allowed,
     * enabled, recipients, ... } } } - see the API's equipment.workflows.js. Anyone in
     * the org reads it; non-managers only get allowed/enabled.
     */
    getOrgEquipmentWorkflows(orgId) {
        return this._equipmentCall(api.get(this._equipment(orgId, "/workflows")))
    }

    /**
     * PUT one workflow's settings ({ enabled, recipients, days, approvers, rules }) -
     * `equipment/workflow_locked` (402) above the tier. Answers like the GET.
     */
    updateOrgEquipmentWorkflow(orgId, workflow, payload) {
        return this._equipmentCall(api.put(this._equipment(orgId, `/workflows/${workflow}`), payload))
    }

    /**
     * GET the org's active units for anyone in it - no rates or logs, plus whether the
     * caller can book each one (canBook, blockedCode, blockedReason, hasAccess).
     */
    getOrgEquipmentDirectory(orgId) {
        return this._equipmentCall(api.get(this._equipment(orgId, "/directory")))
    }

    /**
     * POST a check-out: { person, expectedReturnAt, notes }. Answers with { _id, status,
     * condition, checkoutLog }.
     */
    checkoutOrgEquipment(orgId, equipmentId, payload) {
        return this._equipmentCall(api.post(this._equipment(orgId, `/items/${equipmentId}/checkout`), payload))
    }

    /**
     * POST a check-in (multipart): { conditionOnReturn, notes } plus optional photos.
     */
    checkinOrgEquipment(orgId, equipmentId, { conditionOnReturn, notes }, photos = []) {
        return this._equipmentCall(
            api.uploadFileMulti(this._equipment(orgId, `/items/${equipmentId}/checkin`), photos, {
                fields: { conditionOnReturn, notes },
            }),
        )
    }

    /**
     * POST a maintenance request (multipart): { description, severity } plus optional photos.
     */
    reportOrgEquipmentIssue(orgId, equipmentId, { description, severity }, photos = []) {
        return this._equipmentCall(
            api.uploadFileMulti(this._equipment(orgId, `/items/${equipmentId}/requests/maintenance`), photos, {
                fields: { description, severity },
            }),
        )
    }

    /**
     * POST a request for access to a restricted unit: { reason }.
     */
    requestOrgEquipmentAccess(orgId, equipmentId, payload) {
        return this._equipmentCall(api.post(this._equipment(orgId, `/items/${equipmentId}/requests/access`), payload))
    }

    /**
     * GET maintenance / access requests: { kind, status, equipment, mine }.
     */
    getOrgEquipmentRequests(orgId, params = {}) {
        const qs = new URLSearchParams(
            Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null)),
        ).toString()

        return this._equipmentCall(api.get(this._equipment(orgId, `/requests${qs ? `?${qs}` : ""}`)))
    }

    resolveOrgEquipmentRequest(orgId, requestId, payload) {
        return this._equipmentCall(api.post(this._equipment(orgId, `/requests/${requestId}/resolve`), payload))
    }

    assignOrgEquipmentRequest(orgId, requestId, assignee) {
        return this._equipmentCall(api.put(this._equipment(orgId, `/requests/${requestId}`), { assignee }))
    }

    cancelOrgEquipmentRequest(orgId, requestId, comment) {
        return this._equipmentCall(api.post(this._equipment(orgId, `/requests/${requestId}/cancel`), { comment }))
    }

    /**
     * POST an access decision: { action: "approved" | "rejected", comment, expiresAt }.
     */
    decideOrgEquipmentAccess(orgId, requestId, payload) {
        return this._equipmentCall(api.post(this._equipment(orgId, `/requests/${requestId}/decision`), payload))
    }

    /**
     * POST a booking decision: { action: "approved" | "rejected", comment }.
     */
    decideOrgEquipmentBooking(orgId, bookingId, payload) {
        return this._equipmentCall(api.post(this._equipment(orgId, `/bookings/${bookingId}/decision`), payload))
    }

    /**
     * GET bookings waiting on the current user's approval (all pending ones with `all`,
     * equipment admins only).
     */
    getOrgEquipmentApprovals(orgId, { all } = {}) {
        return this._equipmentCall(api.get(this._equipment(orgId, `/approvals${all ? "?all=true" : ""}`)))
    }

    /**
     * GET the equipment projects ask for - cost lines with an equipment type - and the
     * bookings filling them: the org's requests overlapping { startDate, endDate }
     * (booking days), or one { mission }'s. Each: { _id, title, equipmentType, quantity,
     * startDate, endDate, mission, bookings, days, neededDays, coveredDays, isFilled }.
     */
    getOrgEquipmentDemand(orgId, { startDate, endDate, mission } = {}) {
        const qs = mission ? `mission=${mission}` : `startDate=${+startDate}&endDate=${+endDate}`
        return this._equipmentCall(api.get(this._equipment(orgId, `/demand?${qs}`)))
    }

    /**
     * GET the compliance report for [from, to): calibration, overdue items, blocked
     * units, restricted-access requests.
     */
    getOrgEquipmentCompliance(orgId, { from, to }) {
        return this._equipmentCall(api.get(this._equipment(orgId, `/compliance?from=${from}&to=${to}`)))
    }

    /**
     * GET where the org stands against its Equipment add-on cap - readable by anyone who
     * can see equipment, unlike the admin-only billing state on the org.
     *
     * @param orgId {string} Org _id
     * @return {Promise<{tier: String, tierName: String, quota: Number, used: Number, isTrialing: Boolean}>}
     */
    getOrgEquipmentCapacity(orgId) {
        return api
            .get(`/org/${cleanOrgId(orgId)}/equipment/capacity`)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    /**
     * GET the org's equipment types, archived included (equipment may still point at one).
     * With `mission`, also readable by whoever budgets that mission.
     *
     * @param orgId {string} Org _id
     * @param [params] {{ mission: String }}
     * @return {Promise<Object[]>}
     */
    getOrgEquipmentTypes(orgId, { mission } = {}) {
        return api
            .get(`/org/${cleanOrgId(orgId)}/equipment/types${mission ? `?mission=${mission}` : ""}`)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    /**
     * POST an equipment type
     *
     * @param orgId {string} Org _id
     * @param payload {Object} { name, rate, rateTime, billRate, billUnit }
     * @return {Promise<Object>}
     */
    createOrgEquipmentType(orgId, payload) {
        return api
            .post(`/org/${cleanOrgId(orgId)}/equipment/types`, payload)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    /**
     * PUT an equipment type
     *
     * @param orgId {string} Org _id
     * @param typeId {string} Equipment type _id
     * @param payload {Object}
     * @return {Promise<Object>}
     */
    updateOrgEquipmentType(orgId, typeId, payload) {
        return api
            .put(`/org/${cleanOrgId(orgId)}/equipment/types/${typeId}`, payload)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    /**
     * GET equipment bookings overlapping a window - see equipment.booking.controller.js.
     * Dates are ms timestamps; a booking's endDate is exclusive (the start of the day after).
     *
     * @param orgId {string} Org _id
     * @param params {Object} { startDate, endDate, [equipment]: string[], [mission]: string[] }
     * @return {Promise<Object[]>} bookings with `mission` populated
     */
    getOrgEquipmentBookings(orgId, { startDate, endDate, equipment, mission } = {}) {
        const qs = new URLSearchParams({ startDate: +startDate, endDate: +endDate })

        ;(equipment || []).forEach((id) => qs.append("equipment", id))
        ;(mission || []).forEach((id) => qs.append("mission", id))

        return api
            .get(`/org/${cleanOrgId(orgId)}/equipment/bookings?${qs}`)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    /**
     * POST an equipment booking - refused with `equipment/booking_conflict` (409) when the
     * unit is already booked on any of the days.
     *
     * @param orgId {string} Org _id
     * @param payload {Object} { equipment, mission, startDate, endDate, title, notes }
     * @return {Promise<Object>}
     */
    createOrgEquipmentBooking(orgId, payload) {
        return api
            .post(`/org/${cleanOrgId(orgId)}/equipment/bookings`, payload)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    /**
     * PUT an equipment booking - moves, resizes, re-assigns.
     *
     * @param orgId {string} Org _id
     * @param bookingId {string} Booking _id
     * @param payload {Object}
     * @return {Promise<Object>}
     */
    updateOrgEquipmentBooking(orgId, bookingId, payload) {
        return api
            .put(`/org/${cleanOrgId(orgId)}/equipment/bookings/${bookingId}`, payload)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    /**
     * DELETE an equipment booking
     *
     * @param orgId {string} Org _id
     * @param bookingId {string} Booking _id
     * @return {Promise<Object>}
     */
    deleteOrgEquipmentBooking(orgId, bookingId) {
        return api
            .delete(`/org/${cleanOrgId(orgId)}/equipment/bookings/${bookingId}`)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    /**
     * ============================================================
     * Org Cost Centers
     * ============================================================
     */

    /**
     * POST a new org cost center
     *
     * @param orgId {string} Org _id
     * @param payload {Object}
     * @param payload.code {String} Cost center code, e.g. "CC-100"
     * @param payload.title {String} e.g. "Marketing"
     * @param [payload.externalId] {String} Matching cost center id in the org's CRM/ERP
     * @param [payload.department] {String}
     * @param [payload.status] {String} "active" | "inactive" - defaults to "active"
     * @return {Promise<*>}
     */
    createOrgCostCenter(orgId, payload) {
        return api
            .post(`/org/${cleanOrgId(orgId)}/costCenter`, payload)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    /**
     * PUT an org cost center
     *
     * @param orgId {string} Org _id
     * @param costCenterId {string} Cost center _id
     * @param payload {Object}
     * @return {Promise<Object>}
     */
    updateOrgCostCenter(orgId, costCenterId, payload) {
        return api
            .put(`/org/${cleanOrgId(orgId)}/costCenter/${costCenterId}`, payload)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    /**
     * POST an org tax code
     *
     * @param orgId {string} Org _id
     * @param payload {Object}
     * @param payload.code {String} e.g. "VAT24"
     * @param payload.name {String} e.g. "VAT 24%"
     * @param payload.rate {Number} Percentage, 0-100
     * @param [payload.type] {String} "vat" | "sales_tax" | "withholding" | "exempt" - defaults to "vat"
     * @param [payload.country] {String} ISO 3166-1 alpha-2
     * @param [payload.glCode] {String} GL code _id the tax posts to
     * @param [payload.externalId] {String} Matching tax code id in the org's ERP
     * @param [payload.status] {String} "active" | "inactive" - defaults to "active"
     * @return {Promise<*>}
     */
    createOrgTaxCode(orgId, payload) {
        return api
            .post(`/org/${cleanOrgId(orgId)}/taxCode`, payload)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    /**
     * PUT an org tax code
     *
     * @param orgId {string} Org _id
     * @param taxCodeId {string} Tax code _id
     * @param payload {Object}
     * @return {Promise<Object>}
     */
    updateOrgTaxCode(orgId, taxCodeId, payload) {
        return api
            .put(`/org/${cleanOrgId(orgId)}/taxCode/${taxCodeId}`, payload)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    /**
     * ============================================================
     * Org Client
     * ============================================================
     */

    /**
     * POST a new org client
     *
     * @param orgId {string} Org _id
     * @param payload {Object}
     * @param payload.title {String} Client title
     * @param [payload.about] {String} About the client (description)
     * @param [payload.domains] {Array<String>} Array of client domains, of the format "domain.com" (INVALID: www.domain.com, http://domain.com)
     * @return {Promise<*>}
     */
    createOrgClient(orgId, payload) {
        return api
            .post(`/org/${cleanOrgId(orgId)}/client`, payload)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    /**
     * PUT an org client
     *
     * @param orgId {string} Org _id
     * @param clientId {string} Client _id
     * @param payload {Object}
     * @param payload.title {String} Client title
     * @param [payload.about] {String} About the client (description)
     * @param [payload.domains] {Array<String>} Array of client domains, of the format "domain.com" (INVALID: www.domain.com, http://domain.com)
     * @return {Promise<Object>}
     */
    updateOrgClient(orgId, clientId, payload) {
        return api
            .put(`/org/${cleanOrgId(orgId)}/client/${clientId}`, payload)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    /**
     * DELETE an org client
     *
     * @param orgId {string} Org _id
     * @param clientId {string} Client _id
     * @return {Promise<Object>}
     */
    deleteOrgClient(orgId, clientId) {
        return api
            .delete(`/org/${cleanOrgId(orgId)}/client/${clientId}`)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    uploadClientLogo(orgId, clientId, file) {
        return api
            .uploadFileSingle(`/org/${cleanOrgId(orgId)}/client/${clientId}/logo`, file, {
                name: "orgClientLogo",
            })
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    /**
     * ============================================================
     * Org Client Purchase Orders
     * ============================================================
     */

    /**
     * ============================================================
     * Org Client Invoice
     * ============================================================
     */

    /**
     * POST a new org client invoice
     *
     * @param orgId {string} Org _id
     * @param clientId {string} Org client _id
     *
     * @param payload {Object} Invoice data
     * @param payload.number {number}
     * @param payload.amount {number}
     * @param [payload.status] {string} Defaults to "draft"
     * @param [payload.taxAPercent] {number}
     * @param [payload.taxBPercent] {number}
     * @param [payload.items] {object[]}
     * @param [payload.discountPercent] {number}
     * @param [options] {object}
     * @param [options.qbo] {'on'|'off'} If set to "on", it will sync to quickbooks. Defaults to "off"
     * @param [qbo] {object}
     * @param [qbo.CurrencyRef] {object} Required if multi-currency is enabled for the company.
     * @param qbo.CurrencyRef.value {string}
     * @param [qbo.CurrencyRef.name] {string}
     * @param [qbo.CustomerRef] {object}
     * @param qbo.CustomerRef.value {string}
     * @param [qbo.CustomerRef.name] {string}
     * @param [qbo.TaxCodeRef] {object}
     * @param qbo.TaxCodeRef.value {string}
     * @param [qbo.TaxCodeRef.name] {string}
     * @param [qbo.Description] {string}
     *
     * @return {Promise<*>}
     */
    createOrgClientInvoice(orgId, clientId, payload, options = {}, qbo = {}) {
        const qs = queryString.stringify(options || {})

        return api
            .post(`/clientInvoice/org/${cleanOrgId(orgId)}/client/${clientId}?${qs}`, {
                payload,
                qbo,
            })
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    /**
     * GET an org client invoice list
     *
     * @param orgId {string} Org _id
     * @param [query] {object} Optional query options
     * @param [query.clientId] {string}
     * @param [query.status] {string}
     * @return {Promise<Object[]>}
     */
    getOrgClientInvoiceList(orgId, query = {}) {
        const qs = queryString.stringify(query)
        return api
            .get(`/clientInvoice/org/${cleanOrgId(orgId)}/list?${qs}`)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    /**
     * PUT a client invoice
     *
     * @param clientInvoiceId {string} Client _id
     *
     * @param payload {Object} Invoice data
     * @param payload.number {number}
     * @param payload.amount {number}
     * @param [payload.status] {string} Defaults to "draft"
     * @param [payload.taxAPercent] {number}
     * @param [payload.taxBPercent] {number}
     * @param [payload.items] {object[]}
     * @param [payload.discountPercent] {number}
     * @param [options] {object}
     * @param [options.qbo] {'on'|'off'} If set to "on", it will sync to quickbooks. Defaults to "off"
     *
     * @return {Promise<Object>}
     */
    updateOrgClientInvoice(clientInvoiceId, payload, options = {}) {
        const qs = queryString.stringify(options || {})
        return api
            .put(`/clientInvoice/${clientInvoiceId}?${qs}`, payload)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    // A draft credit note for the whole invoice (every line negated), in the client's credit note series
    createOrgClientCreditNote(clientInvoiceId) {
        return api
            .post(`/clientInvoice/${clientInvoiceId}/credit-note`)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    /**
     * DELETE a client invoice
     *
     * @param clientInvoiceId {string} Client _id
     * @param [options] {object}
     * @param [options.qbo] {'on'|'off'} If set to "on", it will sync to quickbooks. Defaults to "off"
     * @return {Promise<Object>}
     */
    deleteOrgClientInvoice(clientInvoiceId, options = {}) {
        const qs = queryString.stringify(options || {})
        return api
            .delete(`/clientInvoice/${clientInvoiceId}?${qs}`)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    /**
     * Multer upload
     * @param clientInvoiceId
     * @param pdfFile {File} https://developer.mozilla.org/en-US/docs/Web/API/File
     * @returns {Promise<* | void>}
     */
    uploadOrgClientInvoicePdf(clientInvoiceId, pdfFile, options = {}) {
        return api
            .uploadFileSingle(`/clientInvoice/${clientInvoiceId}/pdf`, pdfFile, {
                ...options,
                name: "pdf",
            })
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    /**
     * @param clientInvoiceId {string}
     * @param [options] {object}
     * @param [options.paymentLink] {'stripe'|'paypal'}
     */
    sendOrgClientInvoiceEmail(clientInvoiceId, options = {}) {
        const qs = queryString.stringify(options)
        return api
            .post(`/clientInvoice/${clientInvoiceId}/send-email?${qs}`)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    /**
     * https://stripe.com/docs/api/payment_links/payment_links/create
     *
     * @param clientInvoiceId {string}
     * @param [data] {object}
     *
     * @param [data.lineItems] {object[]}
     * @param [data.lineItems[].productName] {string} The product’s name, meant to be displayable to the customer.
     * @param [data.lineItems[].unitAmount] {number} A positive integer in cents (or 0 for a free price) representing how much to charge.
     * @param [data.lineItems[].quantity] {number} The quantity of the line item being purchased.
     * @param [data.lineItems[].unitLabel] {string} A label that represents units of this product in Stripe and on customers’ receipts and invoices.
     * @param [data.lineItems[].productStatementDescriptor] {string} An arbitrary string to be displayed on your customer’s credit card or bank statement. For validation: https://stripe.com/docs/api/prices/create#create_price-product_data-statement_descriptor
     * Tax needs to be enabled by the connected account and Stripe collects a minimum of 0.5% fee.
     * If not, no taxes are collected.
     * https://stripe.com/docs/tax/set-up
     * @param [data.lineItems[].taxCode] {string} A Stripe tax code to collect taxes: https://stripe.com/docs/tax/tax-categories
     * @param [data.lineItems[].taxBehavior] {'inclusive'|'exclusive'|'unspecified'} Specifies whether the price is considered inclusive of taxes or exclusive of taxes.
     * @param [data.taxCode] {string} A Stripe tax code to collect taxes: https://stripe.com/docs/tax/tax-categories
     * @param [data.taxBehavior] {'inclusive'|'exclusive'|'unspecified'} Specifies whether the price is considered inclusive of taxes or exclusive of taxes.
     * @param [data.automaticTaxEnabled] {boolean} f true, tax will be calculated automatically using the customer’s location.
     * //
     * @param [data.currency] {string} Three-letter ISO currency code. If not provided, will try to use the invoice's currency or the Stripe's account default currency.
     * @param [data.customSubmitText] {string} Custom text that should be displayed alongside the payment confirmation button. Text may be up to 500 characters in length.
     *
     * @param [options] {object}
     * @param [options.integration] {'stripe'} The integration to use to generate the payment link. Defaults to "stripe"
     * @returns {Promise<AxiosResponse<any>>}
     */
    createInvoicePaymentLink(clientInvoiceId, data, options = {}) {
        const qs = queryString.stringify(options)
        return api
            .post(`/clientInvoice/${clientInvoiceId}/payment-link?${qs}`, data)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    /**
     * ============================================================
     * Org Mission
     * ============================================================
     */

    /**
     * Returns historical of org mission, plus all items with "missionId" set to org mission id.
     * ONLY org Admins can view this information.
     *
     * @param orgId
     * @param [options] {Object}
     * @param [options.limit] {String} The limit of results. Defaults to 100.
     * @param [options.page] {String} Paginate results to specified page, starting from page 1 as default.
     * @param [options.sort] {('asc'|'desc')} Sort either ascending or descending (by date). Defaults to desc.
     * @param [options.createdAtFrom] {Date|Number} Date range history was recorded
     * @param [options.createdAtTo] {Date|Number} Date range history was recorded
     * @returns {Promise<any>}
     */
    getMissionAuditTree(orgId, options = {}) {
        const qs = queryString.stringify(options)
        return api
            .get(`/org/${cleanOrgId(orgId)}/audit/tree?${qs}`)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    /**
     * @param orgId
     * @param params {Object}
     * @param params.personId {string|string[]} One or more people IDs assigned to the plan items
     * @param [params.startDate] {Date|Number} Optional start date range
     * @param [params.endDate] {Date|Number} Optional end date range
     * @returns {Promise<any>}
     */
    getPeopleScheduleList(orgId, params = {}) {
        return api
            .post(`/org/${cleanOrgId(orgId)}/schedule/people`, params)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    /**
     *
     * Returns org's mission tree with limited fields for performance
     *
     * @param orgId
     * @return {Promise<any>}
     */
    getOrgMissionTreeLight(orgId) {
        return api
            .get(`/org/${cleanOrgId(orgId)}/mission/overview`)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    /**
     * ============================================================
     * Org Departments
     * ============================================================
     */

    /**
     * @param orgId {string}
     * @param payload {object|object[]}
     * @param payload.title {string}
     * @param [payload.description] {string}
     * @return {Promise<any>}
     */
    addOrgDepartment(orgId, payload) {
        const qs = queryString.stringify({
            withArchivedDepartments: "1",
            withArchivedOffices: "1",
            withArchivedPrograms: "1",
            withArchivedRoles: "1",
        })
        return api
            .post(`/org/${cleanOrgId(orgId)}/department?${qs}`, payload)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    /**
     * @param orgId {string}
     * @param departmentId {string}
     * @param payload {object}
     * @param payload.title {string}
     * @param [payload.description] {string}
     * @return {Promise<any>}
     */
    updateOrgDepartment(orgId, departmentId, payload) {
        const qs = queryString.stringify({
            withArchivedDepartments: "1",
            withArchivedOffices: "1",
            withArchivedPrograms: "1",
            withArchivedRoles: "1",
        })
        return api
            .put(`/org/${cleanOrgId(orgId)}/department/${departmentId}?${qs}`, payload)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    /**
     * @param orgId {string}
     * @param departmentId {string}
     * @return {Promise<any>}
     */
    removeOrgDepartment(orgId, departmentId) {
        const qs = queryString.stringify({
            withArchivedDepartments: "1",
            withArchivedOffices: "1",
            withArchivedPrograms: "1",
            withArchivedRoles: "1",
        })
        return api
            .delete(`/org/${cleanOrgId(orgId)}/department/${departmentId}?${qs}`)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    /**
     * ============================================================
     * Org Offices
     * ============================================================
     */

    /**
     * @param orgId {string}
     * @param payload {object|object[]}
     * @param payload.title {string}
     * @param [payload.description] {string}
     * @param [payload.color] {string}
     * @param [payload.address] {string}
     * @param [payload.country] {string}
     * @return {Promise<any>}
     */
    addOrgOffice(orgId, payload) {
        const qs = queryString.stringify({
            withArchivedDepartments: "1",
            withArchivedOffices: "1",
            withArchivedPrograms: "1",
            withArchivedRoles: "1",
        })
        return api
            .post(`/org/${cleanOrgId(orgId)}/office?${qs}`, payload)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    /**
     * @param orgId {string}
     * @param officeId {string}
     * @param payload {object|object[]}
     * @param payload.title {string}
     * @param [payload.description] {string}
     * @param [payload.color] {string}
     * @param [payload.address] {string}
     * @param [payload.country] {string}
     * @return {Promise<any>}
     */
    updateOrgOffice(orgId, officeId, payload) {
        const qs = queryString.stringify({
            withArchivedDepartments: "1",
            withArchivedOffices: "1",
            withArchivedPrograms: "1",
            withArchivedRoles: "1",
        })
        return api
            .put(`/org/${cleanOrgId(orgId)}/office/${officeId}?${qs}`, payload)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    /**
     * @param orgId {string}
     * @param officeId {string}
     * @return {Promise<any>}
     */
    removeOrgOffice(orgId, officeId) {
        const qs = queryString.stringify({
            withArchivedDepartments: "1",
            withArchivedOffices: "1",
            withArchivedPrograms: "1",
            withArchivedRoles: "1",
        })
        return api
            .delete(`/org/${cleanOrgId(orgId)}/office/${officeId}?${qs}`)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    /**
     * ============================================================
     * Org Programs
     * ============================================================
     */

    /**
     * @param orgId {string}
     * @param payload {object|object[]}
     * @param payload.title {string}
     * @param [payload.owner] {string}
     * @param [payload.clients] {string[]}
     * @return {Promise<any>}
     */
    addOrgProgram(orgId, payload) {
        const qs = queryString.stringify({
            withArchivedDepartments: "1",
            withArchivedOffices: "1",
            withArchivedPrograms: "1",
            withArchivedRoles: "1",
        })
        return api
            .post(`/org/${cleanOrgId(orgId)}/program?${qs}`, { program: payload })
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    /**
     * @param orgId {string}
     * @param programId {string}
     * @param payload {object}
     * @param [payload.owner] {string}
     * @param [payload.clients] {string[]}
     * @return {Promise<any>}
     */
    updateOrgProgram(orgId, programId, payload) {
        const qs = queryString.stringify({
            withArchivedDepartments: "1",
            withArchivedOffices: "1",
            withArchivedPrograms: "1",
            withArchivedRoles: "1",
        })
        return api
            .put(`/org/${cleanOrgId(orgId)}/program/${programId}?${qs}`, { payload })
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    /**
     * @param orgId {string}
     * @param programId {string}
     * @return {Promise<any>}
     */
    removeOrgProgram(orgId, programId) {
        const qs = queryString.stringify({
            withArchivedDepartments: "1",
            withArchivedOffices: "1",
            withArchivedPrograms: "1",
            withArchivedRoles: "1",
        })
        return api
            .delete(`/org/${cleanOrgId(orgId)}/program/${programId}?${qs}`)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    /**
     * ============================================================
     * Org Roles
     * ============================================================
     */

    /**
     * @param orgId {string}
     * @param payload {object|object[]}
     * @return {Promise<any>}
     */
    addOrgRole(orgId, payload) {
        const qs = queryString.stringify({
            withArchivedDepartments: "1",
            withArchivedOffices: "1",
            withArchivedPrograms: "1",
            withArchivedRoles: "1",
        })
        return api
            .post(`/org/${cleanOrgId(orgId)}/role?${qs}`, payload)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    /**
     * @param orgId {string}
     * @param roleId {string}
     * @param payload {object}
     * @return {Promise<any>}
     */
    updateOrgRole(orgId, roleId, payload) {
        const qs = queryString.stringify({
            withArchivedDepartments: "1",
            withArchivedOffices: "1",
            withArchivedPrograms: "1",
            withArchivedRoles: "1",
        })
        return api
            .put(`/org/${cleanOrgId(orgId)}/role/${roleId}?${qs}`, payload)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    /**
     * @param orgId {string}
     * @param roleId {string}
     * @return {Promise<any>}
     */
    removeOrgRole(orgId, roleId) {
        const qs = queryString.stringify({
            withArchivedDepartments: "1",
            withArchivedOffices: "1",
            withArchivedPrograms: "1",
            withArchivedRoles: "1",
        })
        return api
            .delete(`/org/${cleanOrgId(orgId)}/role/${roleId}?${qs}`)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    /**
     * ============================================================
     * Org Role Plan Items
     * ============================================================
     */

    /**
     * @param orgId {string}
     * @param [options] {object}
     * @param [options.programId] {string|string[]}
     * @param [options.personId] {string|string[]}
     * @param [options.startDate] {number|date} Required if "endDate" is not provided
     * @param [options.endDate] {number|date} Required if "startDate" is not provided
     * @return {Promise<any>}
     */
    gtGetRolePlanItems(orgId, options = {}) {
        const qs = queryString.stringify(options)
        return api
            .get(`/org-role-plan-item/org/${cleanOrgId(orgId)}?${qs}`)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    /**
     * @param orgId {string}
     * @param rolePlanItemIds {string|string[]}
     * @return {Promise<any>}
     */
    gtGetRolePlanItemById(orgId, rolePlanItemIds) {
        return api
            .post(`/org-role-plan-item/org/${cleanOrgId(orgId)}/role-plan-item/list`, {
                rolePlanItemId: rolePlanItemIds,
            })
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    /**
     * @param orgId {string}
     * @return {Promise<any>}
     */
    gtGetMyRolePlanItems(orgId) {
        return api
            .get(`/org-role-plan-item/org/${cleanOrgId(orgId)}/my`)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    /**
     * @param orgId {string}
     * @param payload {object|object[]}
     * @return {Promise<any>}
     */
    gtCreateRolePlanItem(orgId, payload) {
        return api
            .post(`/org-role-plan-item/org/${cleanOrgId(orgId)}`, { payload })
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    /**
     * @param orgId {string}
     * @param payload {object[]} Array of one or more items to update
     * @param payload[]._id {string} Each object with changes need to also include the _id of the item to be updated
     * @return {Promise<any>}
     */
    gtUpdateRolePlanItem(orgId, payload) {
        return api
            .put(`/org-role-plan-item/org/${cleanOrgId(orgId)}/role-plan-item/bulk`, { payload })
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    /**
     * @param orgId {string}
     * @param rolePlanItemId {string}
     * @return {Promise<any>}
     */
    gtDeleteRolePlanItem(orgId, rolePlanItemId) {
        return api
            .delete(`/org-role-plan-item/org/${cleanOrgId(orgId)}/role-plan-item/${rolePlanItemId}`)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    /**
     * @param orgId {string}
     * @param payload {object[]}
     * @param payload[]._id {string} Each object with changes need to also include the _id of the item to be updated
     * @param payload[].status {'draft'|'pending'|'approved'|'rejected'}
     * @param [payload[].context] {string}
     * @param [payload[].comment] {string}
     * @return {Promise<any>}
     */
    changeRolePlanItemStatus(orgId, payload) {
        return api
            .put(`/org-role-plan-item/org/${cleanOrgId(orgId)}/role-plan-item/status`, { payload })
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    /**
     * ============================================================
     * Org App Sumo
     * ============================================================
     */

    /**
     * @param oobCode {string}
     */
    getAppSumoByOobCode(oobCode) {
        return api
            .post(`/org/appsumo/oob-code/doc`, { oobCode })
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    /**
     * Generates a new set of clientId and clientSecret for our OpenAPI.
     * NOTE: This will automatically revoke the previous set of keys, thus access to the OpenAPI. Use with caution.
     * NOTE: The "clientSecret" in the response, is the only time this key gets revealed.
     *
     * @param orgId {string}
     * @return {Promise<any>} E.g. {clientId: 'sdjkcbj...', clientSecret: 'hjbdfhbfd...'}
     */
    createOpenApiKeys(orgId) {
        return api
            .post(`/org/${cleanOrgId(orgId)}/open-api/keys`)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    /**
     * Revokes the keys and access token for OpenAPI.
     * NOTE: This will revoke all access to the OpenAPI. Use with caution.
     *
     * @param orgId {string}
     * @return {Promise<any>} E.g. {clientId: 'sdjkcbj...', clientSecret: 'hjbdfhbfd...'}
     */
    revokeOpenApiKeys(orgId) {
        return api
            .delete(`/org/${cleanOrgId(orgId)}/open-api/keys`)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    /**
     * EXAMPLE:
     * apiOrg.onChanges('update', data => console.log(data))
     * @param operation {string} - update, create or delete
     * @param [query] {Object}
     * @param [cb] {Function}
     */
    onChanges(operation, query, cb) {
        if (typeof query === "function") {
            cb = query
            query = {}
        }

        ;(async () => {
            const socket = await api.socket.connect(`changes/orgs/${operation}`, { query })
            socket.on("changes", cb)
        })()
    }

    onPeopleChanges(operation, query, cb) {
        if (typeof query === "function") {
            cb = query
            query = {}
        }

        ;(async () => {
            const socket = await api.socket.connect(`changes/org.people/${operation}`, { query })
            socket.on("changes", cb)
        })()
    }

    onClientChanges(operation, query, cb) {
        if (typeof query === "function") {
            cb = query
            query = {}
        }

        ;(async () => {
            const socket = await api.socket.connect(`changes/clients/${operation}`, { query })
            socket.on("changes", cb)
        })()
    }

    /**
     * EXAMPLE:
     * const socket = await apiOrg.onGtRoleChanges('update', data => console.log(data))
     * socket.disconnect()
     * @param operation {string} - update, create or delete
     * @param [query] {Object}
     * @param [cb] {Function}
     */
    onGtRoleChanges(operation, query, cb) {
        if (typeof query === "function") {
            cb = query
            query = {}
        }

        let socket
        ;(async () => {
            socket = await api.socket.connect(`changes/org.rolePlanItems/${operation}`, { query })
            socket.on("changes", cb)
        })()

        return {
            disconnect: () => {
                try {
                    socket?.disconnect?.()
                } catch (err) {
                    // Nothing...
                }
            },
        }
    }
}

export default new OrgApi()
